/**
 * Strategy · Plan filter view (#936 D5).
 *
 * Filters Strategy Sections to `SectionType='Plan'` for the active company's
 * Active StrategyDocument and renders them as a stack of section cards.
 *
 * Plan sections expose the AutonomousBoundsJSON metadata when their Policy is
 * `Agent-Autonomous-Within-Bounds`. The card component handles that rendering
 * — this component just loads and routes data + the diff modal.
 */
import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingStrategyDocumentEntity,
    marketingStrategySectionEntity,
} from 'mj_generatedentities';
import { filterByType, type StrategySectionRow } from './strategy-section-helpers';

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-plan',
    template: `
<mkt-ws-page-head
    eyebrow="Strategy · Plan"
    title="Plan sections"
    [sub]="subText">
</mkt-ws-page-head>

<div *ngIf="loading" class="sp__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading plan sections…</div>

<!--
    #938 / #937 — Empty-state CTA. Plan sections derive from the strategic
    Foundations, so the bootstrap path is identical: run the onboarding
    diagnostic. Once Foundations exist the Strategy Agent can fill in the
    Plan side on its next run (or via "Ask the agent" follow-up).
-->
<div *ngIf="!loading && sections.length === 0" class="sp__empty">
    <i class="fa-solid fa-list-check sp__empty-icon"></i>
    <h2 class="sp__empty-title">No Plan sections yet.</h2>
    <p class="sp__empty-sub">
        Plan sections (Objectives, Channel Mix, Budget, Experiment Roadmap,
        Campaigns) build on top of your Foundations. Start the onboarding
        diagnostic to seed Foundations — Plan follows on the next agent run.
    </p>
    <div class="sp__empty-actions">
        <button type="button" class="sp__empty-btn sp__empty-btn--primary"
                (click)="goToDiagnostic()">
            <i class="fa-solid fa-microscope"></i>
            Run onboarding diagnostic
            <i class="fa-solid fa-arrow-right"></i>
        </button>
    </div>
</div>

<div *ngIf="!loading && sections.length > 0" class="sp__layout">
    <mkt-ws-strategy-section-card
        *ngFor="let s of sections"
        [section]="s"
        [canEdit]="canEdit"
        (viewDiffsRequested)="onViewDiffs(s)">
    </mkt-ws-strategy-section-card>
</div>

<mkt-ws-strategy-section-diff-modal
    [open]="diffModalOpen"
    [sectionId]="activeDiffSectionId"
    [sectionTitle]="activeDiffSectionTitle"
    [canEdit]="canEdit"
    (close)="diffModalOpen = false">
</mkt-ws-strategy-section-diff-modal>
    `,
    styles: [`
        :host { display: block; }
        .sp__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .sp__layout { display: flex; flex-direction: column; max-width: 920px; }

        /* Empty-state CTA panel (#938 / #937 wiring). */
        .sp__empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 48px 24px; max-width: 560px; margin: 24px auto 0; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 12px; }
        .sp__empty-icon { font-size: 28px; color: var(--mj-text-secondary, #71717a); margin-bottom: 12px; }
        .sp__empty-title { font-size: 18px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); margin: 0 0 8px; }
        .sp__empty-sub { font-size: 13.5px; line-height: 1.55; color: var(--mj-text-secondary, #71717a); margin: 0 0 20px; }
        .sp__empty-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
        .sp__empty-btn { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 13px; font-weight: 500; font-family: inherit; border-radius: 6px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); cursor: pointer; }
        .sp__empty-btn--primary { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .sp__empty-btn--primary:hover { opacity: 0.92; }
    `]
})
export class StrategyPlanSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.reset();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() companyName = '';
    @Input() canEdit = false;
    @Input() navigate: (path: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    document: marketingStrategyDocumentEntity | null = null;
    sections: StrategySectionRow[] = [];

    diffModalOpen = false;
    activeDiffSectionId: string | null = null;
    activeDiffSectionTitle = '';

    get subText(): string {
        return `${this.sections.length} Plan sections${this.document ? ' · ' + this.document.Status : ''}.`;
    }

    onViewDiffs(s: StrategySectionRow): void {
        this.activeDiffSectionId = s.ID;
        this.activeDiffSectionTitle = s.Title || s.SectionKey;
        this.diffModalOpen = true;
    }

    /** Empty-state CTA — routes to the onboarding diagnostic (#937). */
    goToDiagnostic(): void {
        this.navigate('onboarding/chat');
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const docResult = await rv.RunView<marketingStrategyDocumentEntity>({
                EntityName: 'Strategy Documents',
                ExtraFilter: `CompanyID='${cid}'`,
                MaxRows: 5,
                ResultType: 'simple',
            });
            if (!docResult.Success) {
                LogError(`[StrategyPlanSection] doc query failed: ${docResult.ErrorMessage ?? 'unknown'}`);
                this.reset();
                return;
            }
            const rows = (docResult.Results ?? []) as marketingStrategyDocumentEntity[];
            const filtered = rows.filter(r => r.Status === 'Active' || r.Status === 'Draft');
            filtered.sort((a, b) => b.Version - a.Version);
            this.document = filtered.length > 0 ? filtered[0] : null;

            if (this.document) {
                const sectionResult = await rv.RunView<marketingStrategySectionEntity>({
                    EntityName: 'Strategy Sections',
                    ExtraFilter: `StrategyDocumentID='${this.document.ID}' AND SectionType='Plan'`,
                    OrderBy: 'DisplayOrder ASC',
                    MaxRows: 100,
                    ResultType: 'simple',
                });
                if (sectionResult.Success) {
                    this.sections = filterByType((sectionResult.Results ?? []) as StrategySectionRow[], 'Plan');
                } else {
                    LogError(`[StrategyPlanSection] sections query failed: ${sectionResult.ErrorMessage ?? 'unknown'}`);
                    this.sections = [];
                }
            } else {
                this.sections = [];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyPlanSection] load failed: ${msg}`);
            this.reset();
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private reset(): void {
        this.document = null;
        this.sections = [];
        this.cdr.detectChanges();
    }
}
